import { alphabeticalSorter } from "~/tools/alphabetical-sorter";
import { getTime, minutesToMilliseconds } from "~/tools/date-time";
import type { Time } from "~/tools/date-time/time";
import { pipe } from "~/tools/pipe";
import { sort } from "~/tools/sort";
import { take } from "~/tools/take";

import { type TimeRange, createSpecificRange } from "../app/state/view/timeline/TimeRange";
import {
	type EventBucket,
	type EventCategory,
	type EventItemsWithTotal,
	type GetEventTypesParams,
	type TopologyEvent,
	getEventFn,
	getEventSourcesFn,
	getEventTagsFn,
	getEventTypesFn,
	getEventsFn,
	getEventsHistogramFn,
} from "../common/openapi";

import { type UseFullQueryOptions, type UseQueryOptions, type UseQueryResult, useQuery } from "./react-query";

const EVENT_TIME_WINDOW = minutesToMilliseconds(5);

export const useQueryTopologyEventById = <TResult = TopologyEvent>(
	{
		eventId,
		eventTime,
		timestamp,
	}: {
		eventId: string;
		eventTime: number;
		timestamp?: number;
	},
	options?: UseQueryOptions<TopologyEvent, Error, TResult>,
): UseQueryResult<TResult, Error> => {
	const timeRange = createSpecificRange(eventTime - EVENT_TIME_WINDOW, eventTime + EVENT_TIME_WINDOW);

	return useQuery(
		["events", eventId],
		() =>
			getEventFn({
				eventId,
				startTimestampMs: timeRange.start().getTime(),
				endTimestampMs: timeRange.endDate().getTime(),
				playHeadTimestampMs: timestamp,
			}),
		{ ...options, timeRange, timestamp },
	);
};

const EVENT_ATTRIBUTE_TO_QUERY_FN = {
	types: getEventTypesFn,
	sources: getEventSourcesFn,
	tags: getEventTagsFn,
};

export type EventAttributeKind = keyof typeof EVENT_ATTRIBUTE_TO_QUERY_FN;

export interface QueryEventAttributeParams extends Omit<GetEventTypesParams, "startTimestampMs" | "endTimestampMs"> {
	timeRange: TimeRange;
}

export const queryEventAttribute = ((kind, { playHeadTimestampMs, timeRange, ...params }) =>
	({
		queryKey: ["events", kind, params],
		queryFn: async () => {
			const now = Date.now();
			const getEventAttributeFn = EVENT_ATTRIBUTE_TO_QUERY_FN[kind];

			const { items } = await getEventAttributeFn({
				...params,
				limit: params.limit + 1,
				playHeadTimestampMs,
				startTimestampMs: getTime(timeRange.start(now)),
				endTimestampMs: getTime(timeRange.endDate(now)),
			});

			return {
				// take as many as requested to infer the hasMoreItems flag
				items: pipe(items, take(params.limit), sort(alphabeticalSorter)),
				hasMoreItems: items.length > params.limit,
			};
		},
		timestamp: playHeadTimestampMs,
		timeRange,
	}) as const) satisfies (kind: EventAttributeKind, params: QueryEventAttributeParams) => UseFullQueryOptions;

export interface UseQueryEventCategoriesHistogramParams extends Omit<UseQueryEventsParams, "limit"> {
	bucketCount: number;
}

export type CategorizedEventBucket = Omit<EventBucket, "categoryBuckets">;

export interface EventBucketCategory {
	eventCategory: EventCategory;
	buckets?: Array<CategorizedEventBucket>;
}

export const useQueryEventCategoriesHistogram = <TResult = Array<EventBucketCategory>>(
	{
		topologyQuery,
		includeConnectedComponents,
		eventCategories,
		eventTypes,
		timestamp,
		timeRange,
		bucketCount,
	}: UseQueryEventCategoriesHistogramParams,
	options?: UseQueryOptions<Array<EventBucketCategory>, Error, TResult>,
): UseQueryResult<TResult, Error> => {
	const sortedEventCategories = eventCategories.slice().sort();
	const sortedEventTypes = eventTypes?.slice().sort();

	return useQuery(
		[
			"events",
			"histogram",
			{
				topologyQuery,
				includeConnectedComponents,
				bucketCount,
				eventCategories: sortedEventCategories,
				eventTypes: sortedEventTypes,
			},
		],
		async () => {
			const now = Date.now();

			const { buckets } = await getEventsHistogramFn({
				body: {
					topologyQuery,
					includeConnectedComponents,
					histogramBucketCount: bucketCount,
					eventCategories: sortedEventCategories,
					eventTypes: sortedEventTypes,
					topologyTimestamp: getTime(timestamp),
					startTimestampMs: getTime(timeRange.start(now)),
					endTimestampMs: getTime(timeRange.endDate(now)),
				},
			});

			const bucketsWithCategories = buckets.map((bucket) => ({
				...bucket,
				categories: new Map(bucket.categoryBuckets.map(({ category, count }) => [category, count])),
			}));

			return eventCategories.map((eventCategory) => ({
				eventCategory,
				buckets: bucketsWithCategories.map(({ startTime, endTime, categories }) => ({
					startTime,
					endTime,
					count: categories.get(eventCategory) ?? 0,
				})),
			}));
		},
		{ ...options, timestamp, timeRange },
	);
};

export interface UseQueryEventsParams {
	topologyQuery: string;
	includeConnectedComponents: boolean;
	eventCategories: Array<EventCategory>;
	eventTypes?: Array<string>;
	timestamp: undefined | Time;
	timeRange: TimeRange;
	limit: number;
}

export interface LimitedEventItems extends Pick<EventItemsWithTotal, "items"> {
	hasMore: boolean;
}

export const useQueryEvents = <TResult = LimitedEventItems>(
	{
		topologyQuery,
		includeConnectedComponents,
		eventCategories,
		eventTypes,
		timestamp,
		timeRange,
		limit,
	}: UseQueryEventsParams,
	options?: UseQueryOptions<LimitedEventItems, Error, TResult>,
): UseQueryResult<TResult, Error> => {
	const sortedEventCategories = eventCategories.slice().sort();
	const sortedEventTypes = eventTypes?.slice().sort();

	return useQuery(
		[
			"events",
			{
				limit,
				topologyQuery,
				includeConnectedComponents,
				eventCategories: sortedEventCategories,
				eventTypes: sortedEventTypes,
			},
		],
		async () => {
			const now = Date.now();

			const { items } = await getEventsFn({
				body: {
					limit: limit + 1,
					topologyQuery,
					includeConnectedComponents,
					eventCategories: sortedEventCategories,
					eventTypes: sortedEventTypes,
					playHeadTimestampMs: getTime(timestamp),
					startTimestampMs: getTime(timeRange.start(now)),
					endTimestampMs: getTime(timeRange.endDate(now)),
				},
			});

			return {
				items: items.slice(0, limit),
				hasMore: items.length > limit,
			};
		},
		{ ...options, timestamp, timeRange },
	);
};
